import {RootState} from '@/store';
import {createAsyncThunk, createSlice} from '@reduxjs/toolkit';

import {Flyout} from '@macmillanplatform/ui-core';
import {isObject} from '@macmillanplatform/ui-core/utils';

import {AUTH_CASES, USER_FIELDS} from '../constants';
import type {AuthCase, AuthCaseHandler, LocalAuth} from '../types';

import {clearAuthSession, getAuthCase, readAuthData} from '../helpers';

const name = 'auth';

const isLocalAuth = (val: unknown): val is LocalAuth =>
  isObject(val) &&
  USER_FIELDS.LINK_ACCOUNT in val &&
  USER_FIELDS.ZIP_CODE in val;

const authCases: Record<AuthCase, AuthCaseHandler> = {
  [AUTH_CASES.VALID]: localAuth => localAuth,

  [AUTH_CASES.EXPIRED]: (_, rejectWithValue) => {
    clearAuthSession();
    new Flyout({
      type: 'error',
      children: 'Authentication data expired. Please log in again.',
      timeout: 5000,
    });
    return rejectWithValue(AUTH_CASES.EXPIRED);
  },

  [AUTH_CASES.NONE]: (_, rejectWithValue) => {
    clearAuthSession();
    return rejectWithValue(AUTH_CASES.NONE);
  },
};

export const fetchUser = createAsyncThunk<
  LocalAuth,
  void,
  {rejectValue: AuthCase}
>('auth/user/fetch', (_, {rejectWithValue}) => {
  const local = readAuthData();
  const localAuth = local?.values ?? null;
  const localExpiry = local?.expiry ?? null;
  const authCase = getAuthCase({localAuth, localExpiry});
  const result = authCases[authCase](localAuth, rejectWithValue);

  return isLocalAuth(result) ? result : rejectWithValue(authCase);
});

interface AuthState {
  user: {data: LocalAuth | null; loading: boolean};
}

const initialState: AuthState = {
  user: {data: null, loading: false},
};

const authSlice = createSlice({
  name,
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(fetchUser.pending, state => {
        state.user.loading = true;
      })
      .addCase(fetchUser.fulfilled, (state, {payload}) => {
        state.user.data = payload;
        state.user.loading = false;
      })
      .addCase(fetchUser.rejected, state => {
        state.user.data = null;
        state.user.loading = false;
      });
  },
});

export default authSlice;

export const selectUser = (state: RootState): LocalAuth =>
  state[name].user.data;

export const selectHasUser = (state: RootState): boolean => !!selectUser(state);

export const selectUserField =
  (field: keyof LocalAuth) =>
  (state: RootState): string =>
    selectUser(state)?.[field];

export const selectUserLoading = (state: RootState): boolean =>
  state[name].user.loading;
